/**
 * Form Submissions Page
 *
 * Displays all submissions for a specific form with filtering, export options,
 * and detailed analytics.
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  Eye,
  Inbox,
  BarChart3,
  TrendingUp,
  Download,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import { formatNumber } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Skeleton } from '@/client/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/client/components/ui/dropdown-menu';
import { SubmissionsTable } from '@/client/components/forms/submissions-table';
import type { FormResponse } from '@/shared/schemas/forms';

// =============================================================================
// Export Handlers
// =============================================================================

async function exportSubmissions(
  formId: string,
  formName: string,
  format: 'csv' | 'excel' | 'pdf'
) {
  try {
    const endpoint =
      format === 'csv'
        ? `/api/forms/${formId}/export`
        : `/api/forms/${formId}/export?format=${format}`;

    const response = await apiFetch(endpoint);
    if (!response.ok) throw new Error('Failed to export');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const ext = format === 'excel' ? 'xlsx' : format;
    a.download = `${formName.replace(/\s+/g, '_')}_submissions.${ext}`;

    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast.success(`${format.toUpperCase()} export downloaded`);
  } catch {
    toast.error(`Failed to export as ${format.toUpperCase()}`);
  }
}

// =============================================================================
// Main Component
// =============================================================================

export function FormSubmissionsPage() {
  const { formId } = useParams<{ formId: string }>();

  // Fetch form details
  const { data: form, isLoading: isLoadingForm } = useQuery({
    queryKey: ['form', formId],
    queryFn: async () => {
      const response = await apiFetch(`/api/forms/${formId}`);
      if (!response.ok) throw new Error('Failed to fetch form');
      return response.json() as Promise<FormResponse>;
    },
    enabled: !!formId,
  });

  if (!formId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Form not found</p>
      </div>
    );
  }

  const responseRate =
    form && form.totalViews > 0 ? Math.round((form.totalSubmissions / form.totalViews) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container max-w-7xl mx-auto px-4 py-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/forms" className="hover:text-foreground transition-colors">
              Forms
            </Link>
            <span>/</span>
            {isLoadingForm ? (
              <Skeleton className="h-4 w-32" />
            ) : (
              <span className="text-foreground">{form?.name}</span>
            )}
            <span>/</span>
            <span className="text-foreground">Submissions</span>
          </div>

          {/* Title and Actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/forms">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                    <Inbox className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    {isLoadingForm ? (
                      <>
                        <Skeleton className="h-7 w-48 mb-1" />
                        <Skeleton className="h-4 w-32" />
                      </>
                    ) : (
                      <>
                        <h1 className="text-2xl font-bold">Form Submissions</h1>
                        <p className="text-muted-foreground">{form?.name}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => form && exportSubmissions(formId, form.name, 'csv')}
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => form && exportSubmissions(formId, form.name, 'excel')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export as Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => form && exportSubmissions(formId, form.name, 'pdf')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums">
                    {isLoadingForm ? (
                      <Skeleton className="h-7 w-16" />
                    ) : (
                      formatNumber(form?.totalViews || 0)
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                  <Inbox className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums">
                    {isLoadingForm ? (
                      <Skeleton className="h-7 w-16" />
                    ) : (
                      formatNumber(form?.totalSubmissions || 0)
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Submissions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-950 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums">
                    {isLoadingForm ? <Skeleton className="h-7 w-16" /> : `${responseRate}%`}
                  </p>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <Link
                to={`/analytics?asset=form&id=${formId}`}
                className="flex items-center gap-3 group"
              >
                <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-blue-700 dark:text-blue-300 group-hover:underline">
                    View Detailed Analytics
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    See referrers, devices, locations
                  </p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Submissions Table */}
        {form && <SubmissionsTable formId={formId} />}
        {isLoadingForm && (
          <Card>
            <CardContent className="p-8">
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
